import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faUpRightFromSquare, faUsers, faMusic, faCalendar, faMapMarkerAlt } from '@fortawesome/free-solid-svg-icons'
import api from '@/services/api'
import Spinner from '@/components/Spinner'
import { UserDetailsPanel, UserActionModal } from '@/components/User'
import type { DetailSection, UserAction } from '@/components/User'
import type { Organizer } from '@/components/User'
import { StatusBadge, PlanTypeBadge } from '@/utils/badges'

/**
 * Organizer Details Page (Refactored with reusable components)
 * Dark mode geek aesthetic
 */
export default function OrganizerDetailsPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()

  const [organizer, setOrganizer] = useState<Organizer | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Modal state
  const [modalState, setModalState] = useState<{
    isOpen: boolean
    action: UserAction | null
    isLoading: boolean
  }>({
    isOpen: false,
    action: null,
    isLoading: false,
  })

  // Fetch organizer details
  useEffect(() => {
    if (!id) {
      navigate('/organizers')
      return
    }

    const fetchOrganizerDetails = async () => {
      try {
        const response = await api.get(`/superadmin/users/${id}`)
        if (response.data.success) {
          setOrganizer(response.data.data)
        }
      } catch (err: any) {
        setError(err.response?.data?.error || t('common.error'))
      } finally {
        setLoading(false)
      }
    }

    fetchOrganizerDetails()
  }, [id, navigate, t])

  // Handle action modal open
  const handleAction = (action: UserAction) => {
    setModalState({
      isOpen: true,
      action,
      isLoading: false,
    })
  }

  // Handle action confirm
  const handleConfirmAction = async () => {
    if (!organizer || !modalState.action) return

    setModalState((prev) => ({ ...prev, isLoading: true }))

    try {
      const organizerId = organizer.id

      switch (modalState.action) {
        case 'suspend':
          await api.put(`/superadmin/users/${organizerId}/status`, { status: 'suspended' })
        setOrganizer({ ...organizer, isSuspended: true })
          break

        case 'unsuspend':
          await api.put(`/superadmin/users/${organizerId}/status`, { status: 'active' })
        setOrganizer({ ...organizer, isSuspended: false })
          break

        case 'delete':
          await api.delete(`/superadmin/users/${organizerId}`)
        navigate('/organizers')
        return

        case 'reset-password':
          await api.post(`/superadmin/users/${organizerId}/reset-password`)
          break

        default:
          break
      }

      setModalState({ isOpen: false, action: null, isLoading: false })
    } catch (err: any) {
      setError(err.response?.data?.error || t('common.error'))
      setModalState({ isOpen: false, action: null, isLoading: false })
    }
  }

  // Define detail sections
  const detailSections: DetailSection[] = [
    {
      key: 'identity',
      label: 'Informations d\'identité',
      rows: [
        { key: 'email', label: t('common.email') },
        { key: 'firstName', label: t('common.firstName'), render: (org) => org.profile?.firstName || '—' },
        { key: 'lastName', label: t('common.lastName'), render: (org) => org.profile?.lastName || '—' },
        { key: 'organization', label: t('superadmin.company'), render: (org) => org.profile?.organization || '—' },
        {
          key: 'createdAt',
          label: 'Date d\'inscription',
          render: (org) => new Date(org.createdAt).toLocaleDateString('fr-FR'),
        },
        {
          key: 'status',
          label: t('common.status'),
          render: (org) => (
            <span
              className={`px-3 py-1 rounded-full text-xs font-bold inline-block ${
                org.isSuspended
                  ? 'bg-red-900/50 text-red-300 border border-red-700/50'
                  : 'bg-green-900/50 text-green-300 border border-green-700/50'
              }`}
            >
              {org.isSuspended ? t('common.suspended') : t('common.active')}
            </span>
          ),
        },
      ],
    },
    {
      key: 'stats',
      label: 'Statistiques & Activité',
      rows: [
        {
          key: 'eventCount',
          label: 'Événements',
          render: (org) => org.stats?.eventCount || 0,
        },
        {
          key: 'singerCount',
          label: 'Chanteurs',
          render: (org) => org.stats?.singerCount || 0,
        },
        {
          key: 'lastLogin',
          label: 'Dernière activité',
          render: (org) => (org.stats?.lastLogin ? new Date(org.stats.lastLogin).toLocaleDateString('fr-FR') : '—'),
        },
        {
          key: 'updatedAt',
          label: 'Mise à jour',
          render: (org) => new Date(org.updatedAt || org.createdAt).toLocaleDateString('fr-FR'),
        },
        {
          key: 'subscriptionType',
          label: 'Plan',
          render: (org) => (
            <span className="px-2 py-1 bg-purple-900/30 rounded text-xs">
              {org.subscription?.type === 'free' ? 'Gratuit' : org.subscription?.type || '—'}
            </span>
          ),
        },
        {
          key: 'subscriptionStatus',
          label: 'Statut abonnement',
          render: (org) => org.subscription?.status || '—',
        },
        {
          key: 'subscriptionPeriodEnd',
          label: 'Fin période facturation',
          render: (org) => {
            const periodEnd = org.subscription?.subscriptionPeriodEnd
            if (periodEnd) {
              const periodEndDate = new Date(periodEnd)
              return periodEndDate.toLocaleDateString('fr-FR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              })
            }
            return <span className="text-slate-500">—</span>
          },
        },
        {
          key: 'subscriptionCancellation',
          label: 'Annulation',
          render: (org) => {
            const status = org.subscription?.status
            const expiresAt = org.subscription?.subscriptionExpiresAt

            if (status === 'canceled' && expiresAt) {
              const expirationDate = new Date(expiresAt)
              const now = new Date()
              const isExpired = expirationDate < now

              return (
                <div className="space-y-1">
                  <span className={`px-2 py-1 rounded text-xs font-semibold inline-block ${
                    isExpired
                      ? 'bg-red-900/50 text-red-300 border border-red-700/50'
                      : 'bg-orange-900/50 text-orange-300 border border-orange-700/50'
                  }`}>
                    {isExpired ? 'Expiré' : 'En instance d\'annulation'}
                  </span>
                  <p className="text-xs text-slate-400 mt-1">
                    Date effective : {expirationDate.toLocaleDateString('fr-FR', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </p>
                </div>
              )
            }

            return <span className="text-slate-500">—</span>
          },
        },
      ],
    },
  ]

  // Format revenue amount in French format
  const formatRevenue = (amount: number | null | undefined): string => {
    if (amount === null || amount === undefined || isNaN(amount)) {
      return '€0,00'
    }
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount)
  }

  // Calculate revenue stats from payments
  const calculateRevenueStats = () => {
    if (!organizer?.payments || organizer.payments.length === 0) {
      return {
        totalRevenue: 0,
        paymentsCount: 0,
        lastPayment: null,
        completedPayments: [],
      }
    }

    const completedPayments = organizer.payments.filter((p) => p.status === 'completed')
    const totalRevenue = completedPayments.reduce((sum, p) => sum + (p.amount || 0), 0)
    const lastPayment = organizer.payments.length > 0 ? organizer.payments[0] : null // Already sorted by purchasedAt DESC

    return {
      totalRevenue,
      paymentsCount: organizer.payments.length,
      lastPayment,
      completedPayments,
    }
  }

  const revenueStats = calculateRevenueStats()

  // Add revenue section if organizer has payments or is an organizer
  if (organizer?.role === 'organizer') {
    detailSections.push({
      key: 'revenue',
      label: 'Revenus & Paiements',
      rows: [
        {
          key: 'totalRevenue',
          label: 'Total revenus',
          render: () => (
            <span className="text-green-400 font-bold text-lg">
              {formatRevenue(revenueStats.totalRevenue)}
            </span>
          ),
        },
        {
          key: 'paymentsCount',
          label: 'Nombre de paiements',
          render: () => <span className="text-slate-300">{revenueStats.paymentsCount}</span>,
        },
        {
          key: 'lastPayment',
          label: 'Dernier paiement',
          render: () => {
            if (!revenueStats.lastPayment) {
              return <span className="text-slate-500">—</span>
            }
            const lastPayment = revenueStats.lastPayment
            return (
              <div className="text-xs space-y-1">
                <p>
                  <span className="text-slate-400">Date:</span>{' '}
                  {new Date(lastPayment.purchasedAt).toLocaleDateString('fr-FR', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </p>
                <p>
                  <span className="text-slate-400">Montant:</span>{' '}
                  <span className="text-green-400 font-bold">
                    {formatRevenue(lastPayment.amount)}
                  </span>
                </p>
                <p>
                  <span className="text-slate-400">Type:</span> <span className="text-slate-300">{lastPayment.type === 'monthly_subscription' ? 'Abonnement mensuel' : 'Événement unique'}</span>
                </p>
              </div>
            )
          },
        },
      ],
    })
  }


  return (
    <div className="space-y-8">
      {/* User Details Panel */}
      <UserDetailsPanel
        user={organizer}
        loading={loading}
        error={error}
        sections={detailSections}
        userType="organizer"
        onAction={handleAction}
        onBack={() => navigate('/organizers')}
        showActions={true}
        isSuspended={organizer?.isSuspended || false}
      />

      {/* Payments Table Section */}
      {organizer?.payments && organizer.payments.length > 0 && (
        <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-700/50">
            <h2 className="text-xl font-bold text-white font-mono">
              {'> '}
              <span className="text-green-400">payments</span>
              {' --detailed'}
            </h2>
            <p className="text-slate-400 text-xs mt-1 font-mono">
              Liste détaillée de tous les paiements ({organizer.payments.length})
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-800/50 border-b border-slate-700/50">
                  <th className="px-6 py-3 text-left font-mono text-cyan-400 text-xs uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left font-mono text-cyan-400 text-xs uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left font-mono text-cyan-400 text-xs uppercase tracking-wider">
                    Plan/Événement
                  </th>
                  <th className="px-6 py-3 text-left font-mono text-cyan-400 text-xs uppercase tracking-wider">
                    Montant
                  </th>
                  <th className="px-6 py-3 text-left font-mono text-cyan-400 text-xs uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-left font-mono text-cyan-400 text-xs uppercase tracking-wider">
                    Stripe ID
                  </th>
                  <th className="px-6 py-3 text-left font-mono text-cyan-400 text-xs uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700/50">
                {organizer.payments.map((payment) => (
                  <tr key={payment.id} className="hover:bg-slate-800/50 transition">
                    <td className="px-6 py-4 text-slate-300 font-mono text-xs">
                      {new Date(payment.purchasedAt).toLocaleDateString('fr-FR', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </td>
                    <td className="px-6 py-4 text-slate-300 font-mono text-xs">
                      {payment.type === 'monthly_subscription' ? (
                        <span className="px-2 py-1 bg-blue-900/30 text-blue-300 rounded text-xs">Abonnement mensuel</span>
                      ) : (
                        <span className="px-2 py-1 bg-purple-900/30 text-purple-300 rounded text-xs">Événement unique</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-slate-400 font-mono text-xs">
                      {payment.eventId ? (
                        <span className="text-cyan-400">Événement #{payment.eventId.slice(-8)}</span>
                      ) : payment.type === 'monthly_subscription' ? (
                        organizer?.subscription?.type === 'free' ? (
                          '—'
                        ) : (
                          <span className="capitalize">{organizer?.subscription?.type || '—'}</span>
                        )
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="px-6 py-4 text-green-400 font-bold font-mono">
                      {formatRevenue(payment.amount)}
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`px-2 py-1 rounded text-xs font-bold ${
                          payment.status === 'completed'
                            ? 'bg-green-900/50 text-green-300 border border-green-700/50'
                            : payment.status === 'active'
                            ? 'bg-blue-900/50 text-blue-300 border border-blue-700/50'
                            : payment.status === 'canceled'
                            ? 'bg-red-900/50 text-red-300 border border-red-700/50'
                            : 'bg-yellow-900/50 text-yellow-300 border border-yellow-700/50'
                        }`}
                      >
                        {payment.status === 'completed' ? 'Complété' : payment.status === 'active' ? 'Actif' : payment.status === 'canceled' ? 'Annulé' : payment.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-slate-500 font-mono text-xs">
                      {payment.stripeSubscriptionId ? (
                        <span className="font-mono text-xs break-all">{payment.stripeSubscriptionId}</span>
                      ) : payment.stripePaymentIntentId ? (
                        <span className="font-mono text-xs break-all">{payment.stripePaymentIntentId}</span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {(payment.stripeSubscriptionId || payment.stripePaymentIntentId) && (
                        <a
                          href={`https://dashboard.stripe.com/test${
                            payment.stripeSubscriptionId
                              ? `/subscriptions/${payment.stripeSubscriptionId}`
                              : `/payments/${payment.stripePaymentIntentId}`
                          }`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-cyan-400 hover:text-cyan-300 transition text-xs font-mono"
                        >
                          <FontAwesomeIcon icon={faUpRightFromSquare} className="mr-1" />
                          Stripe
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Events Table Section */}
      {organizer?.events && organizer.events.length > 0 && (
        <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-700/50">
            <h2 className="text-xl font-bold text-white font-mono">
              {'> '}
              <span className="text-amber-400">events</span>
              {' --list'}
            </h2>
            <p className="text-slate-400 text-xs mt-1 font-mono">
              Liste de tous les événements ({organizer.events.length})
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-800/50 border-b border-slate-700/50">
                  <th className="px-6 py-4 text-left font-mono text-amber-400 text-xs uppercase tracking-wider cursor-pointer hover:bg-slate-700/50">
                    Nom
                  </th>
                  <th className="px-6 py-4 text-left font-mono text-amber-400 text-xs uppercase tracking-wider cursor-pointer hover:bg-slate-700/50">
                    Date
                  </th>
                  <th className="px-6 py-4 text-left font-mono text-amber-400 text-xs uppercase tracking-wider cursor-pointer hover:bg-slate-700/50">
                    Statut
                  </th>
                  <th className="px-6 py-4 text-left font-mono text-amber-400 text-xs uppercase tracking-wider cursor-pointer hover:bg-slate-700/50">
                    Plan
                  </th>
                  <th className="px-6 py-4 text-left font-mono text-amber-400 text-xs uppercase tracking-wider cursor-pointer hover:bg-slate-700/50">
                    Chanteurs
                  </th>
                  <th className="px-6 py-4 text-left font-mono text-amber-400 text-xs uppercase tracking-wider cursor-pointer hover:bg-slate-700/50">
                    Chansons
                  </th>
                  <th className="px-6 py-4 text-left font-mono text-amber-400 text-xs uppercase tracking-wider cursor-pointer hover:bg-slate-700/50">
                    Créé le
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700/50">
                {organizer.events.map((event) => (
                  <tr
                    key={event.id}
                    onClick={() => navigate(`/events/${event.id}`)}
                    className="hover:bg-slate-800/50 transition group cursor-pointer"
                  >
                    <td className="px-6 py-4 text-slate-200 font-semibold">{event.name}</td>
                    <td className="px-6 py-4 text-slate-300 font-mono text-xs">
                      {event.date
                        ? new Date(event.date).toLocaleDateString('fr-FR', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })
                        : '—'}
                    </td>
                    <td className="px-6 py-4">
                      <StatusBadge status={event.status} />
                    </td>
                    <td className="px-6 py-4">
                      <PlanTypeBadge planType={event.planType || 'free'} />
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <FontAwesomeIcon icon={faUsers} className="text-purple-400 text-sm" />
                        <span className="text-slate-300 font-mono">{event.singersCount || 0}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <FontAwesomeIcon icon={faMusic} className="text-cyan-400 text-sm" />
                        <span className="text-slate-300 font-mono">{event.songsCount || 0}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-slate-300 font-mono text-xs">
                      {new Date(event.createdAt).toLocaleDateString('fr-FR')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Venues Table Section */}
      {organizer?.venues && organizer.venues.length > 0 && (
        <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-700/50">
            <h2 className="text-xl font-bold text-white font-mono">
              {'> '}
              <span className="text-cyan-400">venues</span>
              {' --list'}
            </h2>
            <p className="text-slate-400 text-xs mt-1 font-mono">
              Liste de tous les lieux ({organizer.venues.length})
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-800/50 border-b border-slate-700/50">
                  <th className="px-6 py-4 text-left font-mono text-cyan-400 text-xs uppercase tracking-wider cursor-pointer hover:bg-slate-700/50">
                    Nom
                  </th>
                  <th className="px-6 py-4 text-left font-mono text-cyan-400 text-xs uppercase tracking-wider cursor-pointer hover:bg-slate-700/50">
                    Adresse
                  </th>
                  <th className="px-6 py-4 text-left font-mono text-cyan-400 text-xs uppercase tracking-wider cursor-pointer hover:bg-slate-700/50">
                    Ville
                  </th>
                  <th className="px-6 py-4 text-left font-mono text-cyan-400 text-xs uppercase tracking-wider cursor-pointer hover:bg-slate-700/50">
                    Pays
                  </th>
                  <th className="px-6 py-4 text-left font-mono text-cyan-400 text-xs uppercase tracking-wider cursor-pointer hover:bg-slate-700/50">
                    Événements
                  </th>
                  <th className="px-6 py-4 text-left font-mono text-cyan-400 text-xs uppercase tracking-wider cursor-pointer hover:bg-slate-700/50">
                    Créé le
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700/50">
                {organizer.venues.map((venue) => (
                  <tr
                    key={venue.id}
                    onClick={() => navigate(`/venues/${venue.id}`)}
                    className="hover:bg-slate-800/50 transition group cursor-pointer"
                  >
                    <td className="px-6 py-4 text-slate-200 font-semibold">{venue.name}</td>
                    <td className="px-6 py-4 text-slate-300 font-mono text-xs">
                      {venue.address || '—'}
                    </td>
                    <td className="px-6 py-4 text-slate-300 font-mono text-sm">{venue.city || '—'}</td>
                    <td className="px-6 py-4 text-slate-300 font-mono text-xs">
                      {venue.country || '—'}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <FontAwesomeIcon icon={faCalendar} className="text-cyan-400 text-sm" />
                        <span className="text-slate-300 font-mono">{venue.eventsCount || 0}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-slate-300 font-mono text-xs">
                      {new Date(venue.createdAt).toLocaleDateString('fr-FR')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Action Modal */}
      <UserActionModal
        isOpen={modalState.isOpen}
        action={modalState.action}
        userType="organizer"
        userName={
          organizer ? `${organizer.profile?.firstName} ${organizer.profile?.lastName}` : ''
        }
        isLoading={modalState.isLoading}
        onConfirm={handleConfirmAction}
        onCancel={() =>
          setModalState({
            isOpen: false,
            action: null,
            isLoading: false,
          })
        }
      />
    </div>
  )
}
